import { useCallback, useMemo, useState } from 'react'

import { faPen, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import { Modal, Select, VStack } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import {
  FieldTemplateForViewFragment,
  ViewColumnTemplateFragment,
  ViewColumnType,
} from 'types/graphql'

import { ConditionalFormFields } from 'src/components/form/ConditionalFormFields'
import FieldTemplateMultiSelect from 'src/components/TableViews/ManageColumnsDrawer/FieldTemplateMultiSelect'
import {
  useCreateViewCustomColumnTemplateMutation,
  useUpdateViewCustomColumnTemplateMutation,
} from 'src/fetch/views/viewColumnTemplates'
import { useAppForm } from 'src/hooks/use-app-form'
import { UseDisclosureReturn } from 'src/hooks/use-disclosure'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { TableView } from 'src/models/TableViews/TableView'
import { TableViewManager } from 'src/models/TableViews/TableViewManager'
import { FeatureGate, useFeatureFlagging } from 'src/utils/featureFlagging/featureFlagging'
import { getRequiredValidator, useAnnounceFirstInvalidField } from 'src/utils/forms'
import { QueryKey } from 'src/utils/queryClient'

const MODAL_CREATABLE_COLUMN_TYPES = new Set<ViewColumnType>(['CustomTaskField', 'RecordField'])

const COLUMN_TYPE_SUBLABELS: Partial<Record<ViewColumnType, string>> = {
  CustomTaskField: 'Display field values from custom tasks.',
  RecordField: 'Display field values from record application forms.',
}

const FIELD_TEMPLATE_IDS_FIELDS = ['fieldTemplateIds']

type FormValues = {
  columnName: string
  fieldTemplateIds: number[]
}

type Props = {
  modalState: UseDisclosureReturn
  onColumnVisibilityToggled: (value: string) => Promise<unknown>
  tableViewManager: TableViewManager
  view: TableView
} & (
  | {
      mode: 'create'
      columnTemplate?: never
    }
  | {
      mode: 'edit'
      columnTemplate: ViewColumnTemplateFragment
    }
)

const UpdateCustomColumnModal = ({
  columnTemplate,
  modalState,
  mode,
  onColumnVisibilityToggled,
  tableViewManager,
  view,
}: Props) => {
  const { checkFeatureFlag } = useFeatureFlagging()
  const { mutateAsync: createColumn } = useCreateViewCustomColumnTemplateMutation()
  const { mutateAsync: updateColumn } = useUpdateViewCustomColumnTemplateMutation()
  const announceFirstInvalidField = useAnnounceFirstInvalidField()
  const organizationId = useMyOrgId()
  const recordTypeId = tableViewManager.recordTypeId
  const queryClient = useQueryClient()

  const [columnType, setColumnType] = useState<ViewColumnType | undefined>(
    mode === 'edit' ? columnTemplate.columnType : undefined
  )

  const [selectedFieldTemplates, setSelectedFieldTemplates] = useState<
    FieldTemplateForViewFragment[] | undefined
  >(mode === 'edit' ? columnTemplate.fieldTemplates : undefined)

  const defaultValues = useMemo<FormValues>(
    () => ({
      columnName: columnTemplate?.labelTitleCase ?? '',
      fieldTemplateIds: columnTemplate?.fieldTemplates?.map((f) => f.id) ?? [],
    }),
    [columnTemplate]
  )

  const form = useAppForm({ defaultValues })

  const columnTypeOptions: SelectOption<ViewColumnType>[] = useMemo(() => {
    if (mode === 'edit') {
      const ct = view.customizableColumnTemplatesByType.get(columnTemplate.columnType)
      if (!ct) {
        return []
      }
      return [
        {
          label: ct.labelTitleCase,
          sublabel: COLUMN_TYPE_SUBLABELS[ct.columnType],
          value: ct.columnType,
        },
      ]
    }

    return view.customizableColumnTemplates
      .filter((ct) => MODAL_CREATABLE_COLUMN_TYPES.has(ct.columnType))
      .filter((ct) => {
        if (ct.columnType === 'CustomTaskField') {
          return checkFeatureFlag(FeatureGate.CustomTaskFieldColumns)
        }
        return true
      })
      .map((ct) => ({
        label: ct.labelTitleCase,
        sublabel: COLUMN_TYPE_SUBLABELS[ct.columnType],
        value: ct.columnType,
      }))
  }, [
    checkFeatureFlag,
    columnTemplate?.columnType,
    mode,
    view.customizableColumnTemplatesByType,
    view.customizableColumnTemplates,
  ])

  const selectedColumnTypeOption = useMemo(
    () => columnTypeOptions.find((o) => o.value === columnType),
    [columnType, columnTypeOptions]
  )

  const getColumnTypeOptionKey = useCallback((o: SelectOption<ViewColumnType>) => o.value, [])

  const handleColumnTypeChange = useCallback(
    (option: SelectOption<ViewColumnType> | undefined) => {
      setColumnType(option?.value)
      setSelectedFieldTemplates(undefined)
      form.setFieldValue('fieldTemplateIds', [])
      form.setFieldValue('columnName', '')
    },
    [form]
  )

  const handleFieldTemplatesChange = useCallback(
    (templates: FieldTemplateForViewFragment[] | undefined) => {
      setSelectedFieldTemplates(templates)
      form.setFieldValue('fieldTemplateIds', templates?.map((f) => f.id) ?? [])
    },
    [form]
  )

  const handleTaskFieldSelected = useCallback(
    (fieldLabel: string) => {
      form.setFieldValue('columnName', fieldLabel)
    },
    [form]
  )

  const handleSubmit = useCallback(async () => {
    await form.handleSubmit()
    if (!form.state.isValid) {
      announceFirstInvalidField()
      return Promise.reject()
    }

    const columnName = form.getFieldValue('columnName')
    const fieldTemplateIds = form.getFieldValue('fieldTemplateIds')
    if (!columnType) {
      return Promise.reject()
    }
    if (!recordTypeId && !organizationId) {
      return Promise.reject()
    }

    if (mode === 'create') {
      const result = await runInAction(
        async () =>
          await createColumn({
            input: {
              columnType,
              configuredLabel: columnName,
              fieldTemplateIds,
              ...(recordTypeId ? { recordTypeId } : { organizationId }),
              viewDashboardId: tableViewManager.viewDashboardId,
            },
          })
      )
      runInAction(() => {
        tableViewManager.columnTemplateStore.addColumnTemplate(
          result.createViewCustomColumnTemplate
        )
      })
      await onColumnVisibilityToggled(result.createViewCustomColumnTemplate.generatedId)
    } else {
      const result = await runInAction(
        async () =>
          await updateColumn({
            input: {
              generatedId: columnTemplate.generatedId,
              configuredLabel: columnName,
              fieldTemplateIds,
              organizationId,
            },
          })
      )
      runInAction(() => {
        tableViewManager.columnTemplateStore.replaceColumnTemplate(
          result.updateViewCustomColumnTemplate
        )
      })
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKey.ViewsData] }),
      queryClient.invalidateQueries({ queryKey: [QueryKey.ViewsDataCounts] }),
    ])
  }, [
    announceFirstInvalidField,
    columnTemplate?.generatedId,
    columnType,
    createColumn,
    form,
    mode,
    onColumnVisibilityToggled,
    organizationId,
    queryClient,
    recordTypeId,
    tableViewManager.columnTemplateStore,
    tableViewManager.viewDashboardId,
    updateColumn,
  ])

  return (
    <Modal onClose={modalState.close} preventOverlayClose>
      <Modal.Header icon={mode === 'create' ? faPlus : faPen}>
        {mode === 'create' ? 'Create Custom Column' : 'Edit Custom Column'}
      </Modal.Header>
      <Modal.Content>
        <VStack align="stretch" gap={4}>
          <Select<ViewColumnType>
            label="Column Type"
            isDisabled={mode === 'edit'}
            isRequired
            getOptionKey={getColumnTypeOptionKey}
            options={columnTypeOptions}
            selectedOption={selectedColumnTypeOption}
            onSelectedOptionChange={handleColumnTypeChange}
          />
          <ConditionalFormFields
            form={form}
            fields={FIELD_TEMPLATE_IDS_FIELDS}
            show={columnType === 'RecordField'}
          >
            <form.AppField
              name="fieldTemplateIds"
              validators={{
                onBlur({ value }: { value: number[] }) {
                  if (!value?.length) {
                    return 'Please select at least one field'
                  }
                },
              }}
            >
              {(field) => (
                <FieldTemplateMultiSelect
                  errorMessage={field.state.meta.errors.join(', ')}
                  isInvalid={field.state.meta.errors.length > 0}
                  onBlur={field.handleBlur}
                  onChange={handleFieldTemplatesChange}
                  recordTypeId={recordTypeId}
                  value={selectedFieldTemplates}
                />
              )}
            </form.AppField>
          </ConditionalFormFields>
          <ConditionalFormFields
            form={form}
            fields={FIELD_TEMPLATE_IDS_FIELDS}
            show={columnType === 'CustomTaskField'}
          >
            <form.AppField
              name="fieldTemplateIds"
              validators={{
                onBlur({ value }: { value: number[] }) {
                  if (!value?.length) {
                    return 'Please select at least one field'
                  }
                },
              }}
            >
              {(field) => <field.TaskFieldSelect onFieldSelected={handleTaskFieldSelected} />}
            </form.AppField>
          </ConditionalFormFields>
          <form.AppField
            name="columnName"
            validators={getRequiredValidator('Please enter a column name')}
          >
            {(field) => (
              <field.Input
                field={field}
                label="Column Name"
                isRequired
                placeholder="Enter a name for your column"
              />
            )}
          </form.AppField>
        </VStack>
      </Modal.Content>
      <Modal.Footer
        confirmText={mode === 'create' ? 'Create column' : 'Save changes'}
        onConfirm={handleSubmit}
        disabledReason={!columnType ? 'Please select a column type' : undefined}
      />
    </Modal>
  )
}

export default observer(UpdateCustomColumnModal)
